import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SetupPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Setup failed');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F9F9' }}>
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full mx-4 p-8 text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
          <h1 className="text-xl font-bold mb-2" style={{ color: '#1A1209' }}>Invalid Setup Link</h1>
          <p className="text-sm text-gray-500">This link is missing a setup token. Please contact your administrator for a new invite.</p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F9F9' }}>
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full mx-4 p-8 text-center" data-testid="setup-success">
          <CheckCircle2 size={48} className="mx-auto mb-4" style={{ color: '#10B981' }} />
          <h1 className="text-xl font-bold mb-2" style={{ color: '#1A1209' }}>Password Set Successfully!</h1>
          <p className="text-sm text-gray-500 mb-1">You can now log in with your email:</p>
          <p className="text-sm font-medium" style={{ color: '#F5831F' }}>{result.email}</p>
          <button onClick={() => navigate('/login')}
            className="mt-6 w-full py-3 rounded-xl text-white font-semibold text-sm"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="go-to-login-btn">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F9F9' }}>
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full mx-4 overflow-hidden">
        <div className="px-8 pt-8 pb-5 text-center" style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #F5F3FF 100%)' }}>
          <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#F5831F15' }}>
            <Lock size={24} style={{ color: '#F5831F' }} />
          </div>
          <h1 className="text-xl font-bold" style={{ color: '#1A1209' }}>Set Your Password</h1>
          <p className="text-sm mt-1" style={{ color: '#666' }}>You&apos;ve been invited to join the HiBFF admin team. Set a secure password to get started.</p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 pb-8 pt-5 space-y-4">
          <div>
            <label className="text-xs font-medium block mb-1.5" style={{ color: '#666' }}>Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border text-sm pr-10"
                style={{ borderColor: '#E0E0E0' }}
                placeholder="Min. 8 characters"
                required
                data-testid="setup-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium block mb-1.5" style={{ color: '#666' }}>Confirm Password</label>
            <input
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border text-sm"
              style={{ borderColor: '#E0E0E0' }}
              placeholder="Repeat password"
              required
              data-testid="setup-confirm-input" />
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-2" data-testid="setup-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <button type="submit" disabled={submitting}
            className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="setup-submit-btn">
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Setting up...</> : 'Set Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SetupPassword;
